import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Result,
  Select,
  Upload,
  message,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useUploadFiles } from "utils/media";
import { FileTypes } from "enum/file-type";
import { useApplyJobs } from "api/jobs/useApplyJobs";
import { useSaveJobs } from "api/jobs/useSaveJobs";
import { Link } from "react-router-dom";
import { EnumeratorInterface, ExperienceInterface } from "api/enumerator/Profile/dto";

type JobApplyFormProps = {
  id: string | undefined;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isSaveJobs: boolean;
  enumerator_info: EnumeratorInterface | undefined;
  resume: string | null;
  isProfileLoading: boolean;
  questions: {
    isOptional: boolean;
    type: string;
    question: string;
  }[];
  onSaveSuccess: (jobId: string) => void;
};

// --- ADDITION: Helper function to calculate total experience in years ---
const calculateTotalExperience = (experience: ExperienceInterface[] = []): number => {
  let totalMonths = 0;
  const today = new Date();

  experience.forEach(job => {
    // Ensure startDate exists and has a toDate method
    if (job.startDate && typeof (job.startDate as any).toDate === 'function') {
      const startDate = (job.startDate as any).toDate();
      // If currently working or no end date, use today's date. Otherwise, use the provided end date.
      const endDate = job.currentlyWorking || !job.endDate
        ? today
        : (job.endDate as any).toDate();
      
      let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
      months -= startDate.getMonth();
      months += endDate.getMonth();
      totalMonths += months <= 0 ? 0 : months;
    }
  });

  // Return years rounded to one decimal place
  return Math.round((totalMonths / 12) * 10) / 10;
};

const JobApplyForm: React.FC<JobApplyFormProps> = ({
  isModalOpen,
  setIsModalOpen,
  id,
  isSaveJobs,
  enumerator_info,
  resume,
  isProfileLoading,
  questions,
}) => {
  const [form] = Form.useForm();
  const applyJobMutation = useApplyJobs();
  const saveJobsMutation = useSaveJobs();
  const { Option } = Select;

  const {
    onChange,
    uploadImage,
    documentID,
    fileList,
    setFileList,
    setDocumentId,
  } = useUploadFiles();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!enumerator_info) {
        message.error("User information is still loading. Please wait a moment.");
        return;
      }
      if (!id) {
        message.error("Job ID not found.");
        return;
      }
      if (isProfileLoading) {
        message.error("Still loading your profile—please wait.");
        return;
      }
      if (!resume) {
        message.error("Resume not found in profile. Please upload it first.");
        return;
      }

      const questionAnswers = questions.reduce((acc, q) => {
        acc[q.question] = values[q.question];
        return acc;
      }, {} as Record<string, string | boolean>);

      // --- ADDITION: Call the calculator and add the result to the payload ---
      const totalExperienceYears = calculateTotalExperience(enumerator_info.experience);

      const payload = {
        id,
        data: {
          coverLetter: documentID || undefined,
          resume,
          questionAnswers: questionAnswers,
          totalExperienceYears: totalExperienceYears, // Add the calculated value
        },
        enumerator_info: enumerator_info,
      };

      const mutation = isSaveJobs ? saveJobsMutation : applyJobMutation;

      mutation.mutate(payload, {
        onSuccess: () => {
          message.success(isSaveJobs ? "Job saved successfully!" : "Application submitted successfully!");
          window.location.reload();
        },
        onError: (error) => {
          console.error("Error submitting application:", error);
          message.error("Something went wrong. Please try again.");
        },
      });
    } catch (err) {
      // Validation errors are handled by Form.Item
    }
  };

  const getModalContent = () => {
    if (isSaveJobs) {
      return (
        <Result
          status="info"
          title="Are you sure you want to save this job?"
          subTitle="Click 'Save' to add this job to your saved list."
        />
      );
    }

    return (
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label="Cover Letter">
          <Upload.Dragger
            name="files"
            maxCount={1}
            multiple={false}
            onChange={onChange}
            fileList={fileList}
            customRequest={(options) => {
              if (!enumerator_info) {
                message.error("Cannot upload file: User info is still loading.");
                options.onError?.(new Error("User info not loaded"));
                return;
              }
              uploadImage(options, {
                type: FileTypes.COVER_LETTER,
                id: id,
                enumerator_info,
                service: FileTypes.COVER_LETTER,
                tags: ["customer", "documents"],
                description: "Customer document upload",
                isConsentDocument: true,
                shouldNotAllowMultipleFiles: true,
              });
            }}
            beforeUpload={(file) => {
              const isPDF = file.type === "application/pdf";
              if (!isPDF) {
                message.error(`${file.name} is not a PDF file.`);
              }
              return isPDF || Upload.LIST_IGNORE;
            }}
            onRemove={() => {
              setFileList([]);
              setDocumentId(null);
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag file to this area to upload</p>
            <p className="ant-upload-hint">Support for a single PDF upload only.</p>
          </Upload.Dragger>
        </Form.Item>
        <p>
          <strong>
            Resume:{" "}
            {isProfileLoading ? (
              "Loading resume..."
            ) : resume ? (
              " Resume loaded"
            ) : (
              <>
                No resume in your profile. &ensp;
                <Link className="text-blue-500" to="/enumerator/profile/cv" onClick={() => setIsModalOpen(false)}>
                  Upload it now
                </Link>
              </>
            )}
          </strong>
        </p>
        <Divider />
        {questions.map((q, index) => (
          <Form.Item
            key={index}
            label={q.question}
            name={q.question}
            rules={[{ required: !q.isOptional, message: "This question is required." }]}
          >
            {q.type === "boolean" ? (
              <Select placeholder="Select an option">
                <Option value="yes">Yes</Option>
                <Option value="no">No</Option>
              </Select>
            ) : (
              <Input placeholder="Enter your answer" />
            )}
          </Form.Item>
        ))}
      </Form>
    );
  };

  return (
    <Modal
      destroyOnClose
      onCancel={() => {
        setIsModalOpen(false);
        setFileList([]);
        setDocumentId(null);
      }}
      title={isSaveJobs ? "Save this Job" : "Apply for this Job"}
      open={isModalOpen}
      footer={[
        <Button key="cancel" onClick={() => setIsModalOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="saveOrSubmit"
          type="primary"
          onClick={handleSubmit}
          loading={isSaveJobs ? saveJobsMutation.isPending : applyJobMutation.isPending}
        >
          {isSaveJobs ? "Save" : "Submit Application"}
        </Button>,
      ]}
    >
      {getModalContent()}
    </Modal>
  );
};

export default JobApplyForm;